import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/pro-light-svg-icons'

import useOutsideClick from 'hooks/useOutsideClick'
import useSetState from 'hooks/useSetState'

import Box from 'suitTheme/Box'
import Text from 'suitTheme/Text'

import Result from './Result'
import styles from './styles.scss'

const selectItem = (item, props, setState) => {
  const {
    callbacks: { selectItem: selectFn }, setStringOnSelect, closeOnSelect, onChange,
  } = props

  selectFn(item)

  setState({
    filterString: setStringOnSelect ? item.text : '',
  })

  if (onChange) onChange({ target: { value: setStringOnSelect ? item.text : '' } })

  if (closeOnSelect) setState({ showResults: false })
}

const defaultState = {
  filterString: '',
  showResults: false,
}

const InputSearch = (props) => {
  const {
    boxProps, customBlankStateText, disabled, listAction, onChange, placeholder, results, showOnFocus, value,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { showResults } = state

  const contentEl = useRef()
  const [isClickedOutside, setIsClickedOutside] = useOutsideClick(contentEl, { enabled: showResults })

  useEffect(() => {
    if (!showResults && showOnFocus) setIsClickedOutside(false)
    if (isClickedOutside && showResults){
      setState({ showResults: false })
      setIsClickedOutside(false)
    }
  }, [isClickedOutside, showResults])

  const filterString = typeof value === 'undefined' ? state.filterString : value

  return (
    <Box
      alignItems="center"
      backgroundColor={disabled ? 'lightestGrey' : 'white'}
      border="1px solid"
      borderColor="lineColor"
      borderRadius="5px"
      className={styles.root}
      fontSize="medium"
      height={38}
      paddingLeft="large"
      position="relative"
      ref={contentEl}
      {...boxProps}
    >
      <FontAwesomeIcon icon={faSearch} />
      <input
        data-test-id="inputSearch"
        disabled={disabled}
        onChange={onChange ? e => onChange(e) : e => setState({ filterString: e.target.value })}
        onFocus={showOnFocus ? () => setState({ showResults: true }) : null}
        placeholder={placeholder}
        style={{ margin: '0 0 0 4px', width: 'calc(100% - 16px)', height: 36 }}
        value={filterString}
      />

      {((!!filterString.length && !showOnFocus) || showResults) && results && results.groups && (
        <Box className={styles.results} flexDirection="column" paddingX="large" paddingBottom="large">
          {results.groups.map((group) => {
            const { key, title, items } = group
            const filteredItems = items.filter(
              item => item.text && item.text.toLowerCase().includes(filterString.toLowerCase()),
            )
            const hasItems = !!filteredItems.length

            return (
              <Box
                key={key}
                flexDirection="column"
                flexShrink="0"
                marginTop="large"
              >
                <Text color="bodyFontLightColor" fontSize="xxsmall" marginBottom="medium">
                  {title || 'GROUP'}
                </Text>

                {!hasItems && !customBlankStateText && (
                  <Text color="bodyFontLightColor" fontSize="small" paddingY="medium">
                    No items to display.
                  </Text>
                )}

                {!hasItems && !!customBlankStateText && (
                  <Result
                    onClick={() => setState({ showResults: false })}
                    result={{ text: customBlankStateText }}
                  />
                )}

                {hasItems
                  && filteredItems.map(result => (
                    <Result
                      key={result.key}
                      onClick={() => selectItem(result, props, setState)}
                      result={result}
                    />
                  ))}

                {listAction && (
                  <Box marginTop={hasItems ? 'large' : 'medium'}>
                    {listAction}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

InputSearch.propTypes = {
  boxProps: PropTypes.object,
  callbacks: PropTypes.shape({
    selectItem: PropTypes.func,
  }),
  closeOnSelect: PropTypes.bool,
  customBlankStateText: PropTypes.string,
  disabled: PropTypes.bool,
  listAction: PropTypes.node,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  results: PropTypes.shape({
    groups: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.shape({
        iconLeft: PropTypes.node,
        text: PropTypes.string,
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })),
    })),
  }),
  setStringOnSelect: PropTypes.bool,
  showOnFocus: PropTypes.bool,
  value: PropTypes.string,
}

InputSearch.defaultProps = {
  setStringOnSelect: false,
}

export default InputSearch
